import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Switch,
  Upload,
  message,
} from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import {
  CloseOutlined,
  DeleteOutlined,
  PictureOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import axiosInstance from "api/axiosInstance";
import martEmptyImg from "assets/images/mart-empty.png";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  fetchCategories,
  uploadCategoryImage,
} from "store/slices/categorySlice";
import type {
  GroceryPackage,
  PackageFormValues,
  PackageItem,
  ProductOption,
} from "../types";

import { UNIT_OPTIONS } from "shared/constants/units";

const { TextArea } = Input;

const acceptedImageTypes = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
];

const unitOptions = UNIT_OPTIONS;

const emptyItem = (id: number): PackageItem => ({
  id,
  quantity: 1,
  unit: "kg",
  is_required: true,
});

export type PackageFormModalProps = {
  open: boolean;
  mode: "add" | "edit";
  initialPackage?: GroceryPackage | null;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (values: PackageFormValues) => void | Promise<void>;
};

type BasicFields = {
  name: string;
  billing_cycle: "weekly" | "monthly";
  description: string;
  is_active: boolean;
};

type ProductsByRow = Record<number, ProductOption[]>;
type ProductLoadingByRow = Record<number, boolean>;

const getInitialFields = (
  initialPackage?: GroceryPackage | null
): BasicFields => ({
  name: initialPackage?.name || "",
  billing_cycle: initialPackage?.billing_cycle || "monthly",
  description: initialPackage?.description || "",
  is_active: initialPackage?.is_active ?? true,
});

const extractArray = (responseData: unknown): unknown[] => {
  if (Array.isArray(responseData)) return responseData;
  if (!responseData || typeof responseData !== "object") return [];

  const data = responseData as {
    data?: unknown;
    products?: unknown;
    results?: unknown;
  };

  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.products)) return data.products;
  if (Array.isArray(data.results)) return data.results;

  if (data.data && typeof data.data === "object") {
    const nested = data.data as { products?: unknown; results?: unknown; data?: unknown };
    if (Array.isArray(nested.products)) return nested.products;
    if (Array.isArray(nested.results)) return nested.results;
    if (Array.isArray(nested.data)) return nested.data;
  }

  return [];
};

const toNumber = (value: unknown): number | undefined => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const mapProductOption = (value: unknown): ProductOption | null => {
  if (!value || typeof value !== "object") return null;
  const product = value as Record<string, unknown>;
  const id = toNumber(product.id);
  const name = product.name ?? product.product_name ?? product.title;

  if (!id || typeof name !== "string" || !name.trim()) {
    return null;
  }

  return {
    id,
    master_product_id: id,
    name: name.trim(),
    price: toNumber(product.price ?? product.amount),
    unit:
      typeof product.unit === "string"
        ? product.unit
        : typeof product.measurement_unit === "string"
          ? product.measurement_unit
          : typeof product.default_unit === "string"
            ? product.default_unit
            : undefined,
    image:
      typeof product.image === "string"
        ? product.image
        : typeof product.logo === "string"
          ? product.logo
          : undefined,
    category_id: toNumber(product.category_id),
  };
};

export const PackageFormModal: React.FC<PackageFormModalProps> = ({
  open,
  mode,
  initialPackage,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const dispatch = useAppDispatch();
  const { categories, loading: categoriesLoading } = useAppSelector(
    (state) => state.categories
  );
  const [form] = Form.useForm<BasicFields>();
  const [benefits, setBenefits] = useState<string[]>([""]);
  const [items, setItems] = useState<PackageItem[]>([emptyItem(1)]);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [initialImageCleared, setInitialImageCleared] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [productsByRow, setProductsByRow] = useState<ProductsByRow>({});
  const [productLoadingByRow, setProductLoadingByRow] =
    useState<ProductLoadingByRow>({});
  const productSearchTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    if (!open) return;

    form.setFieldsValue(getInitialFields(initialPackage));
    setBenefits(
      initialPackage?.benefits.length ? [...initialPackage.benefits] : [""]
    );
    setItems(
      initialPackage?.items.length
        ? initialPackage.items.map((item) => ({ ...item }))
        : [emptyItem(1)]
    );
    setSelectedImageFile(null);
    setImagePreviewUrl(null);
    setInitialImageCleared(false);
    setProductsByRow({});
    setProductLoadingByRow({});

    if (categories.length === 0 && !categoriesLoading) {
      dispatch(fetchCategories());
    }
  }, [open, initialPackage, form, categories.length, categoriesLoading, dispatch]);

  useEffect(() => {
    if (!open) return;

    items.forEach((item) => {
      const rowId = item.id ?? 0;

      if (rowId && item.category_id && !productsByRow[rowId]) {
        fetchProductsForItem(rowId, item.category_id);
      }
    });
    // Only hydrate edit rows after modal state is initialized.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, items]);

  useEffect(() => {
    const timers = productSearchTimers.current;

    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    if (!selectedImageFile) {
      setImagePreviewUrl(null);
      return;
    }

    const previewUrl = URL.createObjectURL(selectedImageFile);
    setImagePreviewUrl(previewUrl);

    return () => URL.revokeObjectURL(previewUrl);
  }, [selectedImageFile]);

  const displayedImage = imagePreviewUrl || (!initialImageCleared ? initialPackage?.image : "");

  const categoryOptions = useMemo(
    () =>
      categories.map((category) => ({
        label: category.name,
        value: category.id,
      })),
    [categories]
  );

  const nextItemId = useMemo(
    () => items.reduce((max, item) => Math.max(max, item.id || 0), 0) + 1,
    [items]
  );

  const closeModal = () => {
    onClose();
    form.resetFields();
  };

  const fetchProductsForItem = async (
    itemId: number,
    categoryId: number,
    search?: string
  ) => {
    setProductLoadingByRow((current) => ({ ...current, [itemId]: true }));

    try {
      const response = await axiosInstance.get("/master-products", {
        params: {
          category_id: categoryId,
          ...(search?.trim() ? { search: search.trim() } : {}),
        },
      });

      const rawProducts = extractArray(response.data);
      const filtered = rawProducts.filter((product: any) => {
        if (!product || typeof product !== "object") return false;
        
        const matchesCategory = !product.category_id || Number(product.category_id) === categoryId;
        const matchesSearch = !search?.trim() || 
          (product.name && String(product.name).toLowerCase().includes(search.trim().toLowerCase())) ||
          (product.product_code && String(product.product_code).toLowerCase().includes(search.trim().toLowerCase()));
          
        return matchesCategory && matchesSearch;
      });

      const products = filtered
        .map(mapProductOption)
        .filter((product): product is ProductOption => Boolean(product));

      setProductsByRow((current) => ({ ...current, [itemId]: products }));
    } catch {
      setProductsByRow((current) => ({ ...current, [itemId]: [] }));
      message.error("Unable to load products.");
    } finally {
      setProductLoadingByRow((current) => ({ ...current, [itemId]: false }));
    }
  };

  const getProductOptions = (item: PackageItem) => {
    const rowId = item.id || 0;
    const options = productsByRow[rowId] || [];
    const hasSelected = options.some((product) => product.id === item.master_product_id);
    const selectedOption =
      item.master_product_id && item.product_name && !hasSelected
        ? [
            {
              id: item.master_product_id,
              master_product_id: item.master_product_id,
              name: item.product_name,
              unit: item.unit,
              price: item.price ?? item.product_price,
              category_id: item.category_id,
            },
          ]
        : [];

    return [...selectedOption, ...options].map((product) => ({
      label: (
        <span className="package-product-option">
          <span>{product.name || "Unnamed product"}</span>
          {product.price || product.unit ? (
            <small>
              {product.price ? `NGN ${product.price.toLocaleString()}` : ""}
              {product.price && product.unit ? " / " : ""}
              {product.unit || ""}
            </small>
          ) : null}
        </span>
      ),
      value: product.id,
      product,
    }));
  };

  const getCategoryOptions = (item: PackageItem) => {
    const hasSelected = categoryOptions.some(
      (category) => category.value === item.category_id
    );
    const selectedOption =
      item.category_id && item.category_name && !hasSelected
        ? [{ label: item.category_name, value: item.category_id }]
        : [];

    return [...selectedOption, ...categoryOptions];
  };

  const updateBenefit = (index: number, value: string) => {
    setBenefits((current) =>
      current.map((benefit, itemIndex) =>
        itemIndex === index ? value : benefit
      )
    );
  };

  const removeBenefit = (index: number) => {
    setBenefits((current) =>
      current.length === 1
        ? [""]
        : current.filter((_, itemIndex) => itemIndex !== index)
    );
  };

  const updateItem = <K extends keyof PackageItem>(
    id: number,
    key: K,
    value: PackageItem[K]
  ) => {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, [key]: value } : item))
    );
  };

  const handleCategoryChange = (itemId: number, categoryId: number) => {
    const category = categories.find((item) => item.id === categoryId);

    setItems((current) =>
      current.map((item) =>
        item.id === itemId
          ? {
              ...item,
              category_id: categoryId,
              category_name: category?.name || "",
              product_id: 0,
              master_product_id: undefined,
              product_name: "",
              product_price: undefined,
              product_unit: undefined,
              price: undefined,
            }
          : item
      )
    );
    setProductsByRow((current) => ({ ...current, [itemId]: [] }));
    fetchProductsForItem(itemId, categoryId);
  };

  const handleProductChange = (itemId: number, productId: number) => {
    const item = items.find((value) => value.id === itemId);
    const product = (productsByRow[itemId] || []).find(
      (value) => value.id === productId
    );

    if (!item || !product) return;

    setItems((current) =>
      current.map((value) =>
        value.id === itemId
          ? {
              ...value,
              product_id: product.id,
              master_product_id: product.master_product_id,
              product_name: product.name?.trim() || "",
              unit: product.unit || value.unit,
              product_price: product.price,
              product_unit: product.unit,
              price: product.price,
            }
          : value
      )
    );
  };

  const handleProductSearch = (
    itemId: number,
    categoryId: number | undefined,
    searchText: string
  ) => {
    if (!categoryId) return;

    if (productSearchTimers.current[itemId]) {
      clearTimeout(productSearchTimers.current[itemId]);
    }

    productSearchTimers.current[itemId] = setTimeout(() => {
      fetchProductsForItem(itemId, categoryId, searchText);
    }, 350);
  };

  const removeItem = (id: number) => {
    setItems((current) =>
      current.length === 1
        ? [emptyItem(nextItemId)]
        : current.filter((item) => item.id !== id)
    );
  };

  const handleImageSelect = (file: File) => {
    if (!acceptedImageTypes.includes(file.type)) {
      message.error("Upload a PNG, JPG, JPEG, or WEBP image.");
      return Upload.LIST_IGNORE;
    }

    setSelectedImageFile(file);
    setInitialImageCleared(false);
    return false;
  };

  const clearSelectedImage = () => {
    setSelectedImageFile(null);
    setInitialImageCleared(true);
  };

  const submitForm = async () => {
    try {
      const values = await form.validateFields();
      const trimmedBenefits = benefits
        .map((benefit) => benefit.trim())
        .filter(Boolean);
      const trimmedItems = items.map((item) => ({
        ...item,
        category_name: item.category_name?.trim(),
        product_name: item.product_name?.trim(),
        unit: item.unit.trim(),
        quantity: Number(item.quantity),
      }));

      if (trimmedBenefits.length === 0) {
        message.error("Add at least one benefit.");
        return;
      }

      if (trimmedItems.length === 0) {
        message.error("Please add at least one package item.");
        return;
      }

      const invalidItem = trimmedItems.find(
        (item) =>
          !item.category_id ||
          !item.master_product_id ||
          !item.product_name ||
          !item.unit ||
          !Number.isFinite(item.quantity) ||
          item.quantity <= 0
      );

      if (invalidItem) {
        message.error(
          "Each item needs a category, product, quantity greater than 0, and unit."
        );
        return;
      }

      let image = !initialImageCleared ? initialPackage?.image : undefined;

      if (selectedImageFile) {
        setUploadingImage(true);
        const uploadResult = await dispatch(uploadCategoryImage(selectedImageFile));
        setUploadingImage(false);

        if (!uploadCategoryImage.fulfilled.match(uploadResult)) {
          message.error(uploadResult.payload || "Image upload failed.");
          return;
        }

        image = uploadResult.payload;
      }

      await onSubmit({
        name: values.name.trim(),
        billing_cycle: values.billing_cycle,
        image,
        description: values.description.trim(),
        benefits: trimmedBenefits,
        items: trimmedItems,
        is_active: values.is_active,
      });
    } catch {
      setUploadingImage(false);
      return;
    }
  };

  return (
    <Modal
      title={mode === "edit" ? "Edit Package" : "Add Package"}
      open={open}
      onCancel={closeModal}
      footer={null}
      centered
      width={920}
      destroyOnClose
      className="add-mart-modal package-form-modal modal-round"
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="Package Image">
              <div className="package-upload-box">
                {!displayedImage ? (
                  <Upload
                    accept={acceptedImageTypes.join(",")}
                    showUploadList={false}
                    beforeUpload={handleImageSelect}
                    fileList={[] as UploadFile[]}
                  >
                    <div className="package-upload-placeholder">
                      <PictureOutlined className="add-mart-upload-icon" />
                      <span className="add-mart-upload-text">
                        Upload Package Image
                      </span>
                    </div>
                  </Upload>
                ) : (
                  <div className="add-mart-upload-preview">
                    <img
                      src={displayedImage}
                      alt="Package preview"
                      className="add-mart-upload-preview-img"
                      onError={(event) => {
                        event.currentTarget.src = martEmptyImg;
                      }}
                    />
                    <button
                      type="button"
                      className="add-mart-upload-remove"
                      onClick={clearSelectedImage}
                      aria-label="Remove and upload another package image"
                    >
                      <CloseOutlined />
                    </button>
                  </div>
                )}
              </div>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="is_active" label="Status" valuePropName="checked">
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                className="package-status-switch"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label="Package Name"
              rules={[{ required: true, message: "Package name is required." }]}
            >
              <Input
                placeholder="Starter Plan"
                size="large"
                className="input-round"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="billing_cycle"
              label="Billing Cycle"
              rules={[{ required: true, message: "Billing cycle is required." }]}
            >
              <Select
                size="large"
                options={[
                  { label: "Weekly", value: "weekly" },
                  { label: "Monthly", value: "monthly" },
                ]}
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: "Description is required." }]}
            >
              <TextArea
                placeholder="Pre-made grocery bundle for Nigerian households."
                rows={4}
                size="large"
                className="input-round textarea-round"
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Benefits</Divider>
        <div className="package-builder-stack">
          {benefits.map((benefit, index) => (
            <div className="package-benefit-row" key={`benefit-${index}`}>
              <Input
                value={benefit}
                placeholder="Full grocery solution for large families"
                className="input-round"
                onChange={(event) => updateBenefit(index, event.target.value)}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeBenefit(index)}
                aria-label="Remove benefit"
              />
            </div>
          ))}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            className="package-add-line-btn"
            onClick={() => setBenefits((current) => [...current, ""])}
          >
            Add Benefit
          </Button>
        </div>

        <Divider orientation="left">Package Items Builder</Divider>
        <div className="package-items-builder">
          {items.map((item) => {
            const rowId = item.id ?? 0;

            return (
            <div className="package-item-row" key={rowId}>
              <Select
                value={item.category_id}
                placeholder="Category"
                showSearch
                optionFilterProp="label"
                options={getCategoryOptions(item)}
                loading={categoriesLoading}
                notFoundContent={
                  categoriesLoading ? (
                    <span className="package-select-state">
                      <Spin size="small" /> Loading categories...
                    </span>
                  ) : (
                    "No categories found."
                  )
                }
                onChange={(value) => handleCategoryChange(rowId, value)}
              />
              <Select
                value={item.master_product_id || undefined}
                placeholder={
                  item.category_id ? "Product" : "Select category first"
                }
                showSearch
                filterOption={false}
                disabled={!item.category_id}
                loading={productLoadingByRow[rowId]}
                options={getProductOptions(item)}
                notFoundContent={
                  productLoadingByRow[rowId] ? (
                    <span className="package-select-state">
                      <Spin size="small" /> Loading products...
                    </span>
                  ) : (
                    "No products found."
                  )
                }
                onSearch={(value) =>
                  handleProductSearch(rowId, item.category_id, value)
                }
                onChange={(value) => handleProductChange(rowId, value)}
              />
              <InputNumber
                value={item.quantity}
                min={0}
                placeholder="Qty"
                className="package-item-quantity"
                onChange={(value) =>
                  updateItem(rowId, "quantity", Number(value || 0))
                }
              />
              <Select
                value={item.unit}
                options={unitOptions}
                className="package-item-unit"
                popupMatchSelectWidth={240}
                onChange={(value) => updateItem(rowId, "unit", value)}
              />
              <Switch
                checked={item.is_required}
                checkedChildren="Required"
                unCheckedChildren="Optional"
                onChange={(checked) => updateItem(rowId, "is_required", checked)}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeItem(rowId)}
                aria-label="Remove package item"
              />
            </div>
            );
          })}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            className="package-add-line-btn"
            onClick={() =>
              setItems((current) => [...current, emptyItem(nextItemId)])
            }
          >
            Add Item
          </Button>
        </div>

        <Form.Item className="mb-0 add-mart-modal-footer package-modal-footer">
          <Button onClick={closeModal} size="large" disabled={uploadingImage || loading}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            className="markets-add-btn btn-round"
            onClick={submitForm}
            loading={uploadingImage || loading}
          >
            Save Package
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PackageFormModal;
